import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppProvider, useApp } from "@/lib/store";
import { Toaster } from "@/components/ui/sonner";
import { Header, Footer, CartDrawer, AIChat, CommandPalette, ImpersonationBanner } from "@/components/Chrome";
import { ComplianceGate } from "@/components/ComplianceGate";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import PeptideDetail from "@/pages/PeptideDetail";
import Calculator from "@/pages/Calculator";
import Compare from "@/pages/Compare";
import Match from "@/pages/Match";
import COALibrary from "@/pages/COALibrary";
import Verify from "@/pages/Verify";
import Bulk from "@/pages/Bulk";
import Wholesale from "@/pages/Wholesale";
import Partners from "@/pages/Partners";
import MyLab from "@/pages/MyLab";
import Admin from "@/pages/Admin";
import Journal from "@/pages/Journal";
import Auth from "@/pages/Auth";
import CheckoutPage from "@/pages/Checkout";
import PaymentReturn from "@/pages/PaymentReturn";
import MyProgram from "@/pages/MyProgram";
import Cases from "@/pages/Cases";
import VRApp from "@/vr/VRApp";
import InvoicePage from "@/pages/Invoice";

import { getToken } from "@/lib/api";
import { useEffect } from "react";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Guard = ({ roles, children }) => {
  const { user } = useApp();
  if (!user && getToken()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-xs text-cyan-300 animate-pulse tracking-widest">AUTHENTICATING…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (roles && !roles.includes(user.role) && !["super_admin", "admin"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

function Shell() {
  const { mode } = useApp();
  return (
    <div className="bg-obsidian min-h-screen text-white">
      <ScrollToTop />
      <ComplianceGate />
      <Header />
      <ImpersonationBanner />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/peptides" element={<Catalog />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/cases" element={<Cases />} />
          <Route path="/vr" element={<VRApp />} />
          <Route path="/vr/*" element={<VRApp />} />
          <Route path="/invoice/:num" element={<InvoicePage />} />
          <Route path="/payment/success" element={<PaymentReturn />} />
          <Route path="/payment/cancel" element={<PaymentReturn cancel />} />
          <Route path="/shop/:slug" element={<ProductDetail />} />
          <Route path="/peptides/:slug" element={<PeptideDetail />} />
          <Route path="/tools/calculator" element={<Calculator />} />
          <Route path="/tools/calculator/:slug" element={<Calculator />} />
          <Route path="/tools/compare" element={<Compare />} />
          <Route path="/tools/match" element={<Match />} />
          <Route path="/tools/converter" element={<Calculator tab="convert" />} />
          <Route path="/coa" element={<COALibrary />} />
          <Route path="/verify/:batch" element={<Verify />} />
          <Route path="/bulk" element={<Bulk />} />
          <Route path="/wholesale" element={<Wholesale />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:slug" element={<Journal />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/my-lab" element={<Guard><MyLab /></Guard>} />
          <Route path="/my-program" element={<Guard><MyProgram /></Guard>} />
          <Route path="/admin" element={<Guard roles={["super_admin", "admin", "manager"]}><Admin /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <CartDrawer />
      <AIChat />
      <CommandPalette />
      <Toaster richColors position="bottom-right" theme="dark" />
    </div>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
